import React, { useCallback, useState } from 'react';
import type { StringMap } from 'corgi-types';
import { UserType } from 'graphql-ts-types';

import { useTranslatedErrorMessageState } from 'common/hooks/form/error';
import { useLoginFormUrls } from 'common/hooks/form/login';
import { useHomeClubRedirect } from 'common/hooks/signup';
import { Event } from 'common/types/auth';
import { hasHomeClubSubscriptionModalConditions } from 'common/utils/homeclub';
import CheckoutHomeClubModal from '../../cart-summary/components/CtaButtons/CheckoutHomeClubModal';
import DisclaimerText from './DisclaimerText';
import { getRegistrationFormFields } from './Form/utils';
import type { FormData as RegistrationFormData } from './Form/utils';
import { useRegistrationRedirect } from './hooks/registration-redirect';
import RegistrationFormContent, {
  type RegistrationFormContentProps,
} from './RegistrationFormContent';

interface RegistrationFormProps extends Pick<
  RegistrationFormContentProps,
  | 'userFlow'
  | 'appleClientId'
  | 'appleStateToken'
  | 'withAppleFirst'
  | 'homeClubSlices'
  | 'isThirdPartySignUpFlow'
  | 'showHomeClubConsent'
  | 'thirdPartySignUpData'
  | 'referredBy'
> {
  userType: UserType;
  withHomeClubSubscriptionModal?: boolean;
  discountPendingTotal?: number;
}

interface HandleRegistrationSuccessParams {
  queryParams?: StringMap;
  formData?: RegistrationFormData;
  homeClubCode?: string | null;
}

const RegistrationForm = ({
  userFlow,
  referredBy,
  appleClientId,
  homeClubSlices,
  withAppleFirst,
  appleStateToken,
  thirdPartySignUpData,
  showHomeClubConsent = false,
  isThirdPartySignUpFlow = false,
  userType: initialUserType = UserType.Private,
  withHomeClubSubscriptionModal = false,
  discountPendingTotal,
}: RegistrationFormProps) => {
  const [commonError, setCommonError] = useTranslatedErrorMessageState();
  const [isDisabled, setIsDisabled] = useState(false);
  const onSuccessRedirect = useRegistrationRedirect(userFlow);
  const onFailedHomeClubOptIn = useHomeClubRedirect(userFlow);
  const formUrls = useLoginFormUrls(userFlow);

  const [userType, setUserType] = useState(initialUserType);
  const [isHomeClubModalOpen, setIsHomeClubModalOpen] = useState(false);

  const handleRegistrationSuccess = useCallback(
    ({ queryParams, formData, homeClubCode }: HandleRegistrationSuccessParams = {}) => {
      if (
        hasHomeClubSubscriptionModalConditions(withHomeClubSubscriptionModal, discountPendingTotal)
      ) {
        // For registration, check if user opted into HomeClub
        if (
          formData &&
          !getRegistrationFormFields(formData, isThirdPartySignUpFlow).homeClubSubscription
        ) {
          setIsHomeClubModalOpen(true);

          return;
        }

        // For social login, check if user already has HomeClub
        if (!formData && !homeClubCode) {
          setIsHomeClubModalOpen(true);

          return;
        }
      }

      onSuccessRedirect({ queryParams });
    },
    [withHomeClubSubscriptionModal, discountPendingTotal, onSuccessRedirect]
  );

  return (
    <>
      <RegistrationFormContent
        commonError={commonError}
        isThirdPartySignUpFlow={isThirdPartySignUpFlow}
        thirdPartySignUpData={thirdPartySignUpData}
        userFlow={userFlow}
        appleClientId={appleClientId}
        appleStateToken={appleStateToken}
        handleRegistrationSuccess={handleRegistrationSuccess}
        setCommonError={setCommonError}
        referredBy={referredBy}
        withAppleFirst={withAppleFirst}
        onFailedHomeClubOptIn={onFailedHomeClubOptIn}
        showHomeClubConsent={showHomeClubConsent}
        homeClubSlices={homeClubSlices}
        setIsDisabled={setIsDisabled}
        setUserType={setUserType}
        isDisabled={isDisabled}
        userType={userType}
        formUrls={formUrls}
      />
      <DisclaimerText userType={userType} showHomeClubConsent={showHomeClubConsent} />
      {hasHomeClubSubscriptionModalConditions(
        withHomeClubSubscriptionModal,
        discountPendingTotal
      ) && (
        <CheckoutHomeClubModal
          isModalOpen={isHomeClubModalOpen}
          homeClubDiscount={discountPendingTotal}
          eventName={Event.RegistrationFormClick}
        />
      )}
    </>
  );
};

export default RegistrationForm;
